<script lang="ts" context="module">
  export const lockState = writable({
    previous: false,
    next: false,
    perviousLockMessage: '',
    nextLockMessage: ''
  });

  export function topnavLockPrevious(message: string) {
    lockState.update(state => ({...state, previous: true, perviousLockMessage: message}));
  }
  export function topnavLockNext(message: string) {
    lockState.update(state => ({...state, next: true, nextLockMessage: message}));
  }
  export function topnavUnlockPrevious() {
    lockState.update(state => ({...state, previous: false, perviousLockMessage: ''}));
  }
  export function topnavUnlockNext() {
    lockState.update(state => ({...state, next: false, nextLockMessage: ''}));
  }
</script>

<script lang="ts">
  import {onDestroy, onMount} from 'svelte';
  import {menu, step, draftStore, thumbDraft, project, videos, preview, thumbnail, isMobile} from '@/store';
  import {writable} from 'svelte/store';

  import {MENU, STEP} from '@/types/common';
  import type {Video} from '@/types/project';

  import Swal from 'sweetalert2';
  import {randomString} from '@/utils/common';
  import {tooltip} from '@/actions/tippy-action';
  import {CustomTopNavEvent, TopnavEvents, topnav} from '@/utils/top-nav-next-custom-events';
  import {ThumbDraftService, DraftService, appEvents} from '@/services';

  // Components
  import SaveProjectDialog from '@/pages/choose-video-redesign/Dialogs/SaveProjectDialog.svelte';
  import DownloadPreviewButton from './DownloadPreviewButton.svelte';
  import ThumbnailDownloadButton from './ThumbnailDownloadButton.svelte';
  import Dialog from '@/components/dialog/Dialog.svelte';
  import DraftsModule from '@/components/drafts/DraftsModule.svelte';
  import ThumbDraftsModule from '@/components/thumbnail/ThumbDraftsModule.svelte';

  let videoList: Video[] = [];
  let selectedVideo: Video;
  let isNextDisabled = false;
  let isPreviousDisabled = false;
  let isSaveDialogOpen: boolean = false;
  let isSavingProject: boolean = false;
  let isProject: boolean = false;
  let projectName: string = '';

  let nextButton: HTMLButtonElement;
  let previousButton: HTMLButtonElement;

  let transcriptionProgress = 0;
  let transcriptionSettimeoutId: number | NodeJS.Timeout;

  let isTranscribingDialogOpen = false;

  const maxLoaderTimeout = 11000;
  const minLoaderTimeout = 4000;
  const maxTranscriptionProgress = 80;

  const maxProgressAddition = 25;
  const minProgressAddition = 6;

  const onPrev = () => {
    if ($lockState.previous) {
      Swal.fire({
        title: 'Warning',
        text: $lockState.perviousLockMessage,
        icon: 'warning',
        confirmButtonText: 'Ok'
      });

      return;
    }
    window.dispatchEvent(new Event(isPreviousDisabled ? TopnavEvents.disabledPrevious : TopnavEvents.previous));
  };
  const transcriptionLoadingSimulator = () => {
    if (transcriptionSettimeoutId) clearTimeout(transcriptionSettimeoutId);
    transcriptionSettimeoutId = setTimeout(
      () => {
        if (transcriptionProgress >= maxTranscriptionProgress) return;
        const newProgress =
          transcriptionProgress + Math.random() * (maxProgressAddition - minProgressAddition) + minProgressAddition;
        transcriptionProgress = newProgress > 80 ? 80 : newProgress;
        transcriptionLoadingSimulator();
      },
      Math.random() * (maxLoaderTimeout - minLoaderTimeout) + minLoaderTimeout
    );
  };

  const stopTranscriptionProgressSimulation = (videoid: number | null = null) => {
    if (transcriptionSettimeoutId) clearTimeout(transcriptionSettimeoutId);
    transcriptionProgress = 100;
    project
      .getVideo(videoid ? videoid : undefined)
      .then(({video, error}) => {
        if (!video) throw error;

        videos.update(videos => {
          videos.files = videos.files.map(videoItem => {
            if (videoItem.id === video.id) return video;
            return videoItem;
          });
          return videos;
        });

        isTranscribingDialogOpen = false;
        topnav.dispatchEvent({nextDisabled: false, nextDisabledTip: ''});
      })
      .catch(() => {
        isTranscribingDialogOpen = false;
        topnav.dispatchEvent({nextDisabled: false, nextDisabledTip: ''});
        Swal.fire({
          position: 'bottom-end',
          icon: 'error',
          title: 'Could not load transcription',
          showConfirmButton: false,
          timer: 1000,
          toast: true
        });
      });
  };

  const onVideoChange = async (projectVideo: Video) => {
    if (transcriptionSettimeoutId) clearTimeout(transcriptionSettimeoutId);
    transcriptionProgress = 0;
    isTranscribingDialogOpen = false;

    const {transcription} = projectVideo;
    if (transcription && transcription.transcription !== null && transcription.operationId === null) return;

    isTranscribingDialogOpen = true;
    topnav.dispatchEvent({nextDisabled: true, nextDisabledTip: 'Transcription is still processing'});

    transcriptionLoadingSimulator();
  };

  const draftService = new DraftService();
  const thumbDraftService = new ThumbDraftService();

  // this function is shared that's why it's here
  const saveNewProject = async () => {
    try {
      const result = {value: projectName};

      if (result.value == '') {
        return false;
      }

      if (typeof $draftStore.currentCloudDraftId !== 'number' || $draftStore.currentCloudDraftId <= 0) {
        return false;
      }

      await draftService.makeProject($draftStore.currentCloudDraftId as number, result.value);
      draftStore.markDraftAsProject(result.value);

      return true;
    } catch (error) {
      console.error('save project error', error);
      Swal.fire({
        position: 'top-end',
        icon: 'warning',
        title: 'Project not saved',
        showConfirmButton: false,
        timer: 1000,
        toast: true
      });
      return false;
    }
  };

  const saveThumbProject = async () => {
    if ($thumbDraft.isProject) {
      // console.log("saveNewProject", $thumbDraft.currentCloudDraftId)
      let drafftResult = false;
      try {
        drafftResult = await thumbDraftService.rename(String($thumbDraft.currentCloudDraftId), {
          projectName: projectName
        });
      } catch (error) {
        drafftResult = false;
        console.error('save project error', error);
      }

      Swal.fire({
        position: 'top-end',
        icon: !drafftResult ? 'error' : 'success',
        title: !drafftResult ? 'Failed to update project' : 'Project updated',
        showConfirmButton: false,
        timer: 1000,
        toast: true
      });

      return drafftResult;
    }

    try {
      const result = {value: projectName};

      if (result.value == '') {
        return false;
      }

      if (typeof $thumbDraft.currentCloudDraftId !== 'number' || $thumbDraft.currentCloudDraftId <= 0) {
        return false;
      }

      await thumbDraftService.makeProject($thumbDraft.currentCloudDraftId as number, result.value);
      thumbDraft.markDraftAsProject(result.value);

      return true;
    } catch (error) {
      console.error('save project error', error);
      Swal.fire({
        position: 'top-end',
        icon: 'warning',
        title: 'Project not saved',
        showConfirmButton: false,
        timer: 1000,
        toast: true
      });
      return false;
    }
  };

  function back2Step1() {
    step.update(STEP.CHOOSE_VIDEO);
  }

  function saveProject() {
    isSaveDialogOpen = true;
  }

  const projectSubscription = project.subscribe(value => {
    const projectVideo = value.video;
    if (!projectVideo) return;
    selectedVideo = projectVideo;
    onVideoChange(projectVideo);
  });

  const videosSubscription = videos.subscribe(value => {
    videoList = value.files;
  });

  let nextTippyInstance: any;
  let previousTippyInstance: any;

  const TopnavCustomEvent = (event: any) => {
    if (typeof event.detail !== 'object') return;
    const details = (event.detail || {}) as CustomTopNavEvent;

    isNextDisabled = details.nextDisabled ? true : false;
    isPreviousDisabled = details.previousDisabled ? true : false;

    if (nextTippyInstance && nextTippyInstance.destroy) {
      try {
        nextTippyInstance.destroy();
        nextTippyInstance = null;
      } catch (err) {
        console.warn('could not destroy tippy instance [next]', err);
      }
    }
    if (previousTippyInstance && previousTippyInstance.destroy) {
      try {
        previousTippyInstance.destroy();
        previousTippyInstance = null;
      } catch (err) {
        console.warn('Could not destroy tippy instance [previous]', err);
      }
    }

    if (typeof details.nextDisabledTip === 'string' && details.nextDisabledTip !== '' && isNextDisabled) {
      nextTippyInstance = tooltip(nextButton, details.nextDisabledTip);
    }

    if (typeof details.previousDisabledTip === 'string' && details.previousDisabledTip !== '' && isPreviousDisabled) {
      previousTippyInstance = tooltip(previousButton, details.previousDisabledTip);
    }
  };

  window.addEventListener(TopnavEvents.customEvent, TopnavCustomEvent);

  function transcriptionComplete(data: any) {
    stopTranscriptionProgressSimulation(data?.identifier || null);
    isTranscribingDialogOpen = false;
  }

  function transcriptionError() {
    stopTranscriptionProgressSimulation();
    Swal.fire({
      title: 'There was a problem with the transcription',
      text: 'There may have been a problem with the transcription. Please try again or select a different video',
      icon: 'error'
    });
    step.update(STEP.CHOOSE_VIDEO);
    isTranscribingDialogOpen = false;
  }

  const unsubscribeDrafts = draftStore.subscribe(draft => {
    if ($menu === MENU.CLIPS) {
      projectName = (draft.projectName as string)
        ? (draft.projectName as string)
        : `${$project.video?.title || 'Untitled Project'}`;
      isProject = draft.isProject as boolean;
      isSavingProject = draft.isSaving;
    }
  });

  const unSubThumbDrafts = thumbDraft.subscribe(draft => {
    if ($menu === MENU.THUMBNAILS) {
      projectName = (draft.projectName as string)
        ? (draft.projectName as string)
        : `${$thumbnail.video?.title || 'Untitled Project'}`;
      isProject = draft.isProject as boolean;
      isSavingProject = draft.isSaving;
    }
  });

  onMount(() => {
    appEvents.on('transcription-completed', transcriptionComplete);
    appEvents.on('transcription-error', transcriptionError);
  });

  onDestroy(() => {
    appEvents.off('transcription-completed', transcriptionComplete);
    appEvents.off('transcription-error', transcriptionError);
    stopTranscriptionProgressSimulation();

    projectSubscription();
    videosSubscription();
    unsubscribeDrafts();

    window.removeEventListener(TopnavEvents.customEvent, TopnavCustomEvent);
    unSubThumbDrafts();
  });
</script>

<div class="flex h-full w-full flex-col gap-4 pl-4 lg:flex-row">
  {#if $step !== STEP.ADMIN && $step !== STEP.ANALYTICS}
    <div class="flex min-w-0 gap-4">
      <img class="h-10 shrink-0 rounded-lg lg:h-full" src={selectedVideo?.preview} alt="selected-video-thumbnail" />
      <div class="grid w-full min-w-0 max-w-[540px] overflow-hidden text-xs">
        <div class="flex w-full min-w-0 gap-2">
          <button on:click={() => back2Step1()} class="flex items-center gap-1 py-1" title="Start over">
            <i class="fa-solid fa-rotate-right"></i>
            <span class="max-w-[200px] truncate">{selectedVideo?.title || 'No Title'}</span>
          </button>
          <div class="flex items-center">
            <i class="fa-solid fa-angle-right"></i>
          </div>
          <div class="flex min-w-0 items-center">
            <span class="max-w-[240px] truncate">{projectName}</span>
          </div>
        </div>
        {#if !$isMobile}
          <div class="w-full pt-1.5">
            {#if $menu === MENU.THUMBNAILS}
              <ThumbDraftsModule />
              <div class="flex w-full min-w-0 items-center gap-2 whitespace-nowrap">
                {#if $step === STEP.CHOOSE_TEMPLATE}
                  <span class="font-bold">Step 1:</span>
                  <p>Select A Template</p>
                {:else if $step === STEP.PREVIEW}
                  <span class="min-w-0 truncate font-bold">Step 2:</span>
                  <p>Image Preview</p>
                {/if}
              </div>
              <div class="flex h-1 w-full max-w-[360px] gap-1 pt-1">
                <div
                  class="h-1 w-full {$step === STEP.CHOOSE_TEMPLATE || $step === STEP.PREVIEW
                    ? 'bg-rose-500'
                    : 'bg-zinc-300'} rounded-lg"
                ></div>
                <div class="h-1 w-full {$step === STEP.PREVIEW ? 'bg-rose-500' : 'bg-zinc-300'} rounded-lg"></div>
              </div>
            {:else}
              <DraftsModule />
              <div class="flex w-full items-center gap-2">
                {#if $step === STEP.CHOOSE_TEMPLATE}
                  <span class="font-bold">Step 1:</span>
                  <p>Select A Template</p>
                {:else if $step === STEP.SELECT_SUBTITLES}
                  <span class="font-bold">Step 2:</span>
                  <p>Select Subtitles</p>
                {:else if $step === STEP.PREVIEW}
                  <span class="font-bold">Step 3:</span>
                  <p>Clip Preview</p>
                {/if}
              </div>
              <div class="flex h-1 w-full max-w-[360px] gap-1 pt-1">
                <div
                  class="h-1 w-full {$step === STEP.CHOOSE_TEMPLATE ||
                  $step === STEP.SELECT_SUBTITLES ||
                  $step === STEP.PREVIEW
                    ? 'bg-rose-500'
                    : 'bg-zinc-300'} rounded-lg"
                ></div>
                <div
                  class="h-1 w-full {$step === STEP.SELECT_SUBTITLES || $step === STEP.PREVIEW
                    ? 'bg-rose-500'
                    : 'bg-zinc-300'} rounded-lg"
                ></div>
                <div class="h-1 w-full {$step === STEP.PREVIEW ? 'bg-rose-500' : 'bg-zinc-300'} rounded-lg"></div>
              </div>
            {/if}
          </div>
        {/if}
      </div>
    </div>
  {/if}
  {#if !$isMobile}
    <div class="relative ml-auto flex items-center justify-center gap-2">
      {#if isSavingProject}
        <span class="align-middle text-xs text-zinc-700">
          <i class="fa-solid fa-rotate fa-spin-pulse"></i> Saving Changes
        </span>
      {:else if isProject}
        <span class="align-middle text-xs text-zinc-700">
          <i class="fa-solid fa-rotate"></i> Autosaved Changes
        </span>
      {/if}
    </div>
  {/if}
  <div class="relative ml-auto flex w-full shrink-0 gap-2 lg:w-auto {$isMobile ? 'pr-2' : ''}">
    {#if !$isMobile}
      <button
        on:click={() => saveProject()}
        class="flex items-center gap-2 rounded-lg border-zinc-300 px-4 py-2 text-zinc-700 transition-all hover:bg-zinc-200 hover:text-zinc-800"
      >
        Save Changes
      </button>
      <button
        bind:this={previousButton}
        on:click={onPrev}
        class="{isPreviousDisabled
          ? 'border-zinc-200 text-zinc-300'
          : 'border-zinc-300 text-zinc-700 hover:bg-zinc-200 hover:text-zinc-800'} flex items-center gap-2 rounded-lg border px-4 py-2 transition-all"
      >
        <i class="fa-solid fa-chevron-left"></i>
        Previous
      </button>
    {/if}
    {#if $step === STEP.PREVIEW && $menu === MENU.CLIPS}
      <DownloadPreviewButton {saveNewProject} />
    {:else if $step === STEP.PREVIEW && $menu === MENU.THUMBNAILS}
      <ThumbnailDownloadButton saveNewProject={saveThumbProject} />
    {:else}
      <div class="relative">
        <div
          class="{isNextDisabled
            ? 'opacity-0'
            : 'pingAnim'} flex h-full items-center gap-2 rounded-lg border border-rose-500 bg-rose-500 px-4 py-2 text-white transition-all hover:bg-rose-600"
        >
          Next
          <i class="fa-solid fa-chevron-right"></i>
        </div>
        <button
          bind:this={nextButton}
          on:click={() =>
            window.dispatchEvent(new Event(isNextDisabled ? TopnavEvents.disabledNext : TopnavEvents.next))}
          class="absolute left-0 top-0 {isNextDisabled
            ? 'border-zinc-300 text-zinc-400'
            : 'border-rose-500 bg-rose-500 text-white hover:bg-rose-600'} flex h-full items-center gap-2 rounded-lg border px-4 py-2 transition-all"
        >
          Next
          <i class="fa-solid fa-chevron-right"></i>
        </button>
      </div>
    {/if}
  </div>
</div>

<Dialog isDialogOpen={isTranscribingDialogOpen} useIfShow={true}>
  <div class="pointer-events-none flex h-full w-full items-center justify-center">
    <div
      class="pointer-events-auto mx-4 h-auto w-full min-w-[280px] max-w-[400px] rounded-2xl border border-gray-500 bg-white shadow-lg dark:border-zinc-600 dark:bg-zinc-700"
    >
      <div class="flex flex-col justify-center p-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2 py-1.5">
            <span class="text-xl font-bold">Transcribing...</span>
          </div>
          <button
            type="button"
            on:click={() => (isTranscribingDialogOpen = false)}
            class="my-2 ml-auto rounded-lg px-2 hover:bg-zinc-100 dark:hover:bg-zinc-600"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <hr class="border-t border-t-gray-300 dark:border-zinc-600" />
        <div class="grid w-full gap-1 pt-4">
          <div class="flex w-full justify-between text-xs text-zinc-700">
            <span class="text-base font-medium">Progress</span>
            <span>{transcriptionProgress?.toFixed(0)}%</span>
          </div>
          <div class="relative relative w-full overflow-hidden rounded-lg bg-zinc-300">
            <div class="bg-primary" style="width: {transcriptionProgress}%">&nbsp;</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</Dialog>
<SaveProjectDialog
  bind:isSaveDialogOpen
  bind:isProject
  bind:isSavingProject
  draft={$menu === MENU.CLIPS ? $draftStore : $thumbDraft}
  {projectName}
/>

<style>
  .pingAnim {
    animation: pinganimation 500ms forwards;
  }

  @keyframes pinganimation {
    0% {
      scale: 100%;
      opacity: 100%;
    }
    100% {
      scale: 135%;
      opacity: 0%;
    }
  }
</style>
